/**
 * Class description
 *
 * Created: 2017-10-04
 * @author jonas
 */
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {DungeonQuestGameConfig, DungeonQuestSymbols} from "../game/DungeonQuestGameConfig";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {DungeonQuestBaseReelSymbol} from "../game/symbols/DungeonQuestBaseReelSymbol";
import {StateMatrix} from "./StateMatrix";
import {Timeline, TimelineLite} from "gsap";
import {DungeonQuestModeData} from "../game/server/DungeonQuestModeData";


export interface IDungeonQuestAdjacentBetLineWinData extends IBetLineWinData {
    winPosition:boolean[];
}

export class DungeonQuestSymbolUtil {

    public static isHighlightedSpin() {
        return DungeonQuestModeData.instance.highlightedReels;
    }

    public static isHighlightedSym(symbolName:string) {
        return DungeonQuestModeData.instance.transformSymbol != undefined && DungeonQuestModeData.instance.transformSymbol == symbolName;
    }

    public static getSymbolsAtPositions(positions:[number, number][]):StateReelSymbol[] {
        const symbols:StateReelSymbol[] = [];
        for(let position of positions) {
            const symbol:StateReelSymbol | null = Reels.getSymbol(position[0], position[1]);
            if(symbol) {
                symbols.push(symbol);
            }
        }
        return symbols;
    }

    public static getSymbolStateAnimation(symbol:StateReelSymbol, newState:ISymbolStateConfig, fadeDuration:number = 0.001):Timeline | null {
        const animation:Timeline | null = symbol.changeState({
            state : newState,
            fadeInDuration : fadeDuration
        });

        return animation;
    }

    public static getSymbolStateAnimations(symbols:StateReelSymbol[], newState:ISymbolStateConfig, fadeDuration:number = 0.001):Timeline[] {
        const animations:Timeline[] = [];

        for(let symbol of symbols) {
            let animation:Timeline | null = DungeonQuestSymbolUtil.getSymbolStateAnimation(symbol, newState, fadeDuration);

            if(animation != null) {
                animations.push(animation);
            }
        }

        return animations;
    }

    public static getSymbolStateAnimationsPerReel(symbols:DungeonQuestBaseReelSymbol[], newState:ISymbolStateConfig, fadeDuration:number = 0):Timeline[][] {
        const animations:Timeline[][] = [];

        for(let symbol of symbols) {
            let animation:Timeline | null = symbol.changeState({
                state : newState,
                fadeInDuration : fadeDuration
            });

            if(animation != null) {
                if(animations[symbol.reelId!] == null) {
                    animations[symbol.reelId!] = [];
                }
                animations[symbol.reelId!].push(animation);
            }
        }

        return animations;
    }

    public static removeFromArrayAndSetStateToNormal(symbols:StateReelSymbol[]):void {
        while(symbols.length > 0) {
            const symbol = symbols.pop()!;
            symbol.changeState({
                state : GameConfig.instance.SYMBOL_STATES.normal,
            });
        }
    }

    public static getSymbolsWithStateInReel(symbolStates:StateMatrix<string>, state:string, reelIndex:number):DungeonQuestBaseReelSymbol[] {
        const symbols:DungeonQuestBaseReelSymbol[] = [];
        const coordinates:[number, number][] = symbolStates.getCoordinatesWithStateInReel(reelIndex, state);
        for(let i:number = 0; i < coordinates.length; i++) {
            const reelIndex:number = coordinates[i][0];
            const symbolIndex:number = coordinates[i][1];
            const symbol:DungeonQuestBaseReelSymbol | null = <DungeonQuestBaseReelSymbol>Reels.getSymbol(reelIndex, symbolIndex);
            if(symbol != null) {
                symbols.push(symbol);
            }
        }
        return symbols;
    }

    public static getSymbolsWithState(symbolStates:StateMatrix<any>, state:any):DungeonQuestBaseReelSymbol[] {
        const symbols:DungeonQuestBaseReelSymbol[] = [];
        const coordinates:[number, number][] = symbolStates.getAllCoordinatesWithState(state);
        for(let i:number = 0; i < coordinates.length; i++) {
            const reelIndex:number = coordinates[i][0];
            const symbolIndex:number = coordinates[i][1];
            const symbol:DungeonQuestBaseReelSymbol | null = <DungeonQuestBaseReelSymbol>Reels.getSymbol(reelIndex, symbolIndex);
            if(symbol != null) {
                symbols.push(symbol);
            }
        }
        return symbols;
    }

    public static createSymbolStateMatrixFromBetWins(betWinsData:IBetLineWinData[]):StateMatrix<string> {
        let states:StateMatrix<string> = new StateMatrix<string>(5, 7, [0, 6], DungeonQuestGameConfig.instance.SYMBOL_STATES.noWin.keyword);

        for(let i:number = 0; i < betWinsData.length; i++) {
            let betLineWinData:IDungeonQuestAdjacentBetLineWinData = <IDungeonQuestAdjacentBetLineWinData>betWinsData[i];

            for(let j:number = 0; j < betLineWinData.winPosition.length; j++) {
                if(betLineWinData.winPosition[j]) {
                    states.setStateAt(j, betLineWinData.betLine[j], DungeonQuestGameConfig.instance.SYMBOL_STATES.win.keyword);
                }
            }
        }
        return states;
    }

    public static createSymbolStateMatrixFromBetWinsWithWeights(betWinsData:IBetLineWinData[]):StateMatrix<number> {
        let states:StateMatrix<number> = new StateMatrix<number>(5, 7, [0, 6], 0);

        for(let i:number = 0; i < betWinsData.length; i++) {
            let betLineWinData:IDungeonQuestAdjacentBetLineWinData = <IDungeonQuestAdjacentBetLineWinData>betWinsData[i];

            let winSymbolCount = 0;
            for(let j:number = 0; j < betLineWinData.winPosition.length; j++) {
                if(betLineWinData.winPosition[j]) {
                    winSymbolCount++;
                    const posValue = Math.max(1, winSymbolCount - 2);
                    if(posValue > states.getStateAt(j, betLineWinData.betLine[j])) {
                        states.setStateAt(j, betLineWinData.betLine[j], posValue);
                    }
                }

            }
        }
        return states;
    }

    public static getSymbolReplaceAndTransformAnimation(symbol:StateReelSymbol, transformToName:string):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(() => symbol.replaceWith(transformToName, symbol.reelId));
        tl.add(() => symbol.changeState({
            state : DungeonQuestGameConfig.instance.SYMBOL_STATES.transform
        }));

        let delay:number = 1.2;
        if(transformToName == DungeonQuestSymbols.W_STICKY) {
            delay = 2.7
        } else if(transformToName == DungeonQuestSymbols.W) {
            delay = 1.3
        }

        tl.add(() => {
            //this is only to used for timing. the time is length of SYMBOL_STATES.transform animation.
        }, tl.totalDuration() + delay);

        return tl;
    }

    public static findOverlayPositions(overlayReels:string[][]):[number, number][] {
        let overlayWildPositions:[number, number][] = [];

        if(overlayReels) {
            for(let i:number = 0; i < overlayReels.length; i++) {
                let reel:string[] = overlayReels[i];

                for(let j:number = 0; j < reel.length; j++) {
                    if(reel[j]) {
                        overlayWildPositions.push([i, j]);
                    }
                }
            }
        }

        return overlayWildPositions;
    }
}